import React, {FC, useMemo, useState} from "react";
import styled from "styled-components";
import {Colors, useBreakpoint} from "@brg/ui";
import {LayoutConst} from "../../../../core";
import {API, Endpoints} from "@brg/api";
import {GameTypes} from "../../../constants/gameTypes";
import {GameStatuses} from "../../../constants/GameStatuses";
import {useTranslation} from "react-i18next";
import {RectangularButtonFilled, RectangularButtonHollow} from "../../../components/buttons";
import {createPortal} from "react-dom";
import {useGameTimer} from "../../../helpers/useGameTimer";
import {ModerationType} from "../../teams/const/ModerationTypes";

const FixedWrapper = styled.div`
  position: fixed;
  bottom: 64px;
  right: 23px;
  z-index: 100;

  @media (max-width: ${LayoutConst.screen.mac13 }px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    bottom: 72px;
    right: 23px;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    right: 0;
    left: 0;
    margin: 0 auto;
    bottom: 56px;
    width: fit-content;
    display: flex;
    justify-content: center;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    bottom: 38px;
  }
`

const MobileWrapper = styled.div<{bgColor: string, offsetTop: number}>`
  background: ${({bgColor}) => bgColor};
  position: sticky;
  height: 36px;
  width: 100%;
  top: 48px;
  z-index: 1000;
  margin: 16px 0 24px 0;
  padding: 0 16px;

  @media (max-width: ${LayoutConst.screen.s}px) {
    margin: calc(32px + ${({offsetTop}) => offsetTop}px) 0 0 0;
    padding: 0 10px;
  }
`

const ProjectGameInfoWrapper = styled.div<{hasStageButton?: boolean}>`

  display: flex;
  flex-direction: column;
  background: #2C2C2B;
  border-radius: 3px;
  padding: 12px 12px 16px 12px;
  width: 266px;
  align-items: flex-start;
  gap: 12px;

  @media (max-width: ${LayoutConst.screen.mac13 }px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: 216px;
    padding: 8px 8px 12px 8px;
    gap: 8px;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    flex-direction: row;
    gap: 16px;
    padding: 0;
    width: 100%;
    justify-content: center;
    background: transparent;
  }
`

const TimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  user-select: none;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    gap: 4px;
  }
  
  @media (max-width: ${LayoutConst.screen.m}px) {
    align-items: center;
    flex-direction: row;
    gap: 8px;
  }
`

const ProjectDesc = styled.div<{hasStageButton?: boolean}>`
  color: ${Colors.galBlack47};
  font-family: Inter;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  letter-spacing: -0.02em;
  align-self: stretch;

  @media (max-width: ${LayoutConst.screen.mac13 + 1}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 11px;
    line-height: 13px;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    align-self: auto;
    font-size: 15px;
    line-height: 20px;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 13px;
    line-height: 16px;
  }
`

const ProjectValue = styled.div`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  background: #464645;
  align-self: stretch;

  color:#BFBFBF;
  text-align: center;
  font-family: Inter;
  font-size: 17px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px; 
  letter-spacing: -0.02em;
  
  @media (max-width: ${LayoutConst.screen.mac13 + 1}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    flex: 1 0 0;
    padding: 12px;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    font-size: 15px;
    line-height: 20px;
    font-weight: 400;
    color: ${Colors.galBlack47};
    background: transparent;
    padding: 0;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 13px;
    line-height: 16px;
  }
`

const NextStageButton = styled(RectangularButtonFilled)<{ isClosed?: boolean }>`
  padding: 0px 24px;
  height: 38px!important;
  align-self: stretch;
  line-height: 22px;
  font-size: 17px;
  cursor: ${({isClosed}) => isClosed ? 'auto' : 'pointer'};
  border-radius: 3px!important;

  @media (max-width: ${LayoutConst.screen.m}px) {
    height: 28px!important;
    width: auto!important;
    padding: 0px 32px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    height: 24px!important;
    min-height: 24px!important;
    width: calc(100%)!important;
  }
`

const NextStageButtonHollow = styled(RectangularButtonHollow)<{ isClosed?: boolean }>`
  padding: 0px 24px;
  height: 38px!important;
  align-self: stretch;
  line-height: 22px;
  font-size: 17px;
  cursor: ${({isClosed}) => isClosed ? 'auto' : 'pointer'};
  border-radius: 3px!important;

  @media (max-width: ${LayoutConst.screen.m}px) {
    height: 28px!important;
    width: auto!important;
    padding: 0px 32px;
    font-size: 15px;
    line-height: 20px;
    font-weight: 400!important;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    height: 24px!important;
    min-height: 24px!important;
    width: calc(100%)!important;
    font-size: 13px;
    line-height: 16px;
  }
  
  &:disabled {
    background: transparent;
    border: 1px solid ${Colors.galBlack47};
  }
`

interface ProjectGameInfoProps {
    projectId?: string,
    isOwner?: boolean,
    bgColor?: string,
    offsetTop?: number,
    customPollTime?: number,
    pollInfo?: any,
}

export const ProjectGameInfo: FC<ProjectGameInfoProps> = (props) => {

    const {
        isOwner,
        projectId,
        bgColor = '#000000',
        offsetTop = 0,
        customPollTime,
        pollInfo
    } = props;

    const gameTimer = useGameTimer(pollInfo, customPollTime);
    const gameType = useMemo(() => pollInfo?.bestOfPollTimer as GameTypes, [pollInfo])
    const gameStatus = useMemo(() => pollInfo?.status as GameStatuses, [pollInfo])

    const { t } = useTranslation();
    const hasStageButton = useMemo(() => (isOwner && gameStatus !== GameStatuses.draft && gameType == GameTypes.RIGHT_NOW) || gameStatus == GameStatuses.closed, [isOwner, gameStatus, gameType])
    const outer = document.getElementById('game-info-portal');
    const isMobile = useBreakpoint(LayoutConst.screen.m);
    const is744 = useBreakpoint(LayoutConst.screen.s);
    const [isNextStageClicked, setIsNextStageClicked] = useState(false);

    const onStageChange = async () => {

        if (gameStatus === GameStatuses.closed || isNextStageClicked) return;
        setIsNextStageClicked(true);

        await API.request()
            .endpoint(Endpoints.geograffee)
            .url('/api/pools/NextStage')
            .post()
            .body(
                {
                    parentEntityId: projectId,
                    parentEntityType: 'project'
                })
            .send()
            .then((r) => r.json())

        setTimeout(() => window.location.reload(), 5000);
    }

    if (isMobile) return <MobileWrapper bgColor={bgColor} offsetTop={offsetTop}>
        <ProjectGameInfoWrapper hasStageButton={hasStageButton}>
            <TimeWrapper>
                {(!is744 || !hasStageButton) && <ProjectDesc hasStageButton={hasStageButton}>{t('voting.time_remaining')}</ProjectDesc>}
                <ProjectValue>{gameTimer}</ProjectValue>
            </TimeWrapper>
            {hasStageButton && <NextStageButtonHollow disabled={gameStatus === GameStatuses.closed || isNextStageClicked} isClosed={gameStatus === GameStatuses.closed} onClick={onStageChange}>{t(isNextStageClicked ? 'voting.buttons.waiting' : gameStatus === GameStatuses.closed ? 'voting.buttons.game_ended' : 'voting.buttons.next_stage')}</NextStageButtonHollow>}
        </ProjectGameInfoWrapper>
    </MobileWrapper>

    if (!outer) return null;

    return createPortal(<FixedWrapper>
        <ProjectGameInfoWrapper hasStageButton={hasStageButton}>
            <TimeWrapper>
                <ProjectDesc hasStageButton={hasStageButton}>{t('voting.time_remaining')}</ProjectDesc>
                <ProjectValue>{gameTimer}</ProjectValue>
            </TimeWrapper>
            {hasStageButton && <NextStageButton disabled={gameStatus === GameStatuses.closed || isNextStageClicked} isClosed={gameStatus === GameStatuses.closed} onClick={onStageChange}>{t(isNextStageClicked ? 'voting.buttons.waiting' : gameStatus === GameStatuses.closed ? 'voting.buttons.game_ended' : 'voting.buttons.next_stage')}</NextStageButton>}
    </ProjectGameInfoWrapper>
    </FixedWrapper>, outer);
}import React, {FC, useMemo, useState} from "react";
import styled from "styled-components";
import {Colors, useBreakpoint} from "@brg/ui";
import {LayoutConst} from "../../../../core";
import {API, Endpoints} from "@brg/api";
import {GameTypes} from "../../../constants/gameTypes";
import {GameStatuses} from "../../../constants/GameStatuses";
import {useTranslation} from "react-i18next";
import {RectangularButtonFilled, RectangularButtonHollow} from "../../../components/buttons";
import {createPortal} from "react-dom";
import {useGameTimer} from "../../../helpers/useGameTimer";
import {ModerationType} from "../../teams/const/ModerationTypes";

const FixedWrapper = styled.div`
  position: fixed;
  bottom: 64px;
  right: 23px;
  z-index: 100;

  @media (max-width: ${LayoutConst.screen.mac13 }px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    bottom: 72px;
    right: 23px;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    right: 0;
    left: 0;
    margin: 0 auto;
    bottom: 56px;
    width: fit-content;
    display: flex;
    justify-content: center;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    bottom: 38px;
  }
`

const MobileWrapper = styled.div<{bgColor: string, offsetTop: number}>`
  background: ${({bgColor}) => bgColor};
  position: sticky;
  height: 36px;
  width: 100%;
  top: 48px;
  z-index: 1000;
  margin: 16px 0 24px 0;
  padding: 0 16px;

  @media (max-width: ${LayoutConst.screen.s}px) {
    margin: calc(32px + ${({offsetTop}) => offsetTop}px) 0 0 0;
    padding: 0 10px;
  }
`

const ProjectGameInfoWrapper = styled.div<{hasStageButton?: boolean}>`

  display: flex;
  flex-direction: column;
  background: #2C2C2B;
  border-radius: 3px;
  padding: 12px 12px 16px 12px;
  width: 266px;
  align-items: flex-start;
  gap: 12px;

  @media (max-width: ${LayoutConst.screen.mac13 }px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: 216px;
    padding: 8px 8px 12px 8px;
    gap: 8px;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    flex-direction: row;
    gap: 16px;
    padding: 0;
    width: 100%;
    justify-content: center;
    background: transparent;
  }
`

const TimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  user-select: none;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    gap: 4px;
  }
  
  @media (max-width: ${LayoutConst.screen.m}px) {
    align-items: center;
    flex-direction: row;
    gap: 8px;
  }
`

const ProjectDesc = styled.div<{hasStageButton?: boolean}>`
  color: ${Colors.galBlack47};
  font-family: Inter;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  letter-spacing: -0.02em;
  align-self: stretch;

  @media (max-width: ${LayoutConst.screen.mac13 + 1}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 11px;
    line-height: 13px;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    align-self: auto;
    font-size: 15px;
    line-height: 20px;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 13px;
    line-height: 16px;
  }
`

const ProjectValue = styled.div`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  background: #464645;
  align-self: stretch;

  color:#BFBFBF;
  text-align: center;
  font-family: Inter;
  font-size: 17px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px; 
  letter-spacing: -0.02em;
  
  @media (max-width: ${LayoutConst.screen.mac13 + 1}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    flex: 1 0 0;
    padding: 12px;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    font-size: 15px;
    line-height: 20px;
    font-weight: 400;
    color: ${Colors.galBlack47};
    background: transparent;
    padding: 0;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 13px;
    line-height: 16px;
  }
`

const NextStageButton = styled(RectangularButtonFilled)<{ isClosed?: boolean }>`
  padding: 0px 24px;
  height: 38px!important;
  align-self: stretch;
  line-height: 22px;
  font-size: 17px;
  cursor: ${({isClosed}) => isClosed ? 'auto' : 'pointer'};
  border-radius: 3px!important;

  @media (max-width: ${LayoutConst.screen.m}px) {
    height: 28px!important;
    width: auto!important;
    padding: 0px 32px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    height: 24px!important;
    min-height: 24px!important;
    width: calc(100%)!important;
  }
`

const NextStageButtonHollow = styled(RectangularButtonHollow)<{ isClosed?: boolean }>`
  padding: 0px 24px;
  height: 38px!important;
  align-self: stretch;
  line-height: 22px;
  font-size: 17px;
  cursor: ${({isClosed}) => isClosed ? 'auto' : 'pointer'};
  border-radius: 3px!important;

  @media (max-width: ${LayoutConst.screen.m}px) {
    height: 28px!important;
    width: auto!important;
    padding: 0px 32px;
    font-size: 15px;
    line-height: 20px;
    font-weight: 400!important;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    height: 24px!important;
    min-height: 24px!important;
    width: calc(100%)!important;
    font-size: 13px;
    line-height: 16px;
  }
  
  &:disabled {
    background: transparent;
    border: 1px solid ${Colors.galBlack47};
  }
`

interface ProjectGameInfoProps {
    projectId?: string,
    isOwner?: boolean,
    bgColor?: string,
    offsetTop?: number,
    customPollTime?: number,
    pollInfo?: any,
}

export const ProjectGameInfo: FC<ProjectGameInfoProps> = (props) => {

    const {
        isOwner,
        projectId,
        bgColor = '#000000',
        offsetTop = 0,
        customPollTime,
        pollInfo
    } = props;

    const gameTimer = useGameTimer(pollInfo, customPollTime);
    const gameType = useMemo(() => pollInfo?.bestOfPollTimer as GameTypes, [pollInfo])
    const gameStatus = useMemo(() => pollInfo?.status as GameStatuses, [pollInfo])

    const { t } = useTranslation();
    const hasStageButton = useMemo(() => (isOwner && gameStatus !== GameStatuses.draft && gameType == GameTypes.RIGHT_NOW) || gameStatus == GameStatuses.closed, [isOwner, gameStatus, gameType])
    const outer = document.getElementById('game-info-portal');
    const isMobile = useBreakpoint(LayoutConst.screen.m);
    const is744 = useBreakpoint(LayoutConst.screen.s);
    const [isNextStageClicked, setIsNextStageClicked] = useState(false);

    const onStageChange = async () => {

        if (gameStatus === GameStatuses.closed || isNextStageClicked) return;
        setIsNextStageClicked(true);

        await API.request()
            .endpoint(Endpoints.geograffee)
            .url('/api/pools/NextStage')
            .post()
            .body(
                {
                    parentEntityId: projectId,
                    parentEntityType: 'project'
                })
            .send()
            .then((r) => r.json())

        setTimeout(() => window.location.reload(), 5000);
    }

    if (isMobile) return <MobileWrapper bgColor={bgColor} offsetTop={offsetTop}>
        <ProjectGameInfoWrapper hasStageButton={hasStageButton}>
            <TimeWrapper>
                {(!is744 || !hasStageButton) && <ProjectDesc hasStageButton={hasStageButton}>{t('voting.time_remaining')}</ProjectDesc>}
                <ProjectValue>{gameTimer}</ProjectValue>
            </TimeWrapper>
            {hasStageButton && <NextStageButtonHollow disabled={gameStatus === GameStatuses.closed || isNextStageClicked} isClosed={gameStatus === GameStatuses.closed} onClick={onStageChange}>{t(isNextStageClicked ? 'voting.buttons.waiting' : gameStatus === GameStatuses.closed ? 'voting.buttons.game_ended' : 'voting.buttons.next_stage')}</NextStageButtonHollow>}
        </ProjectGameInfoWrapper>
    </MobileWrapper>

    if (!outer) return null;

    return createPortal(<FixedWrapper>
        <ProjectGameInfoWrapper hasStageButton={hasStageButton}>
            <TimeWrapper>
                <ProjectDesc hasStageButton={hasStageButton}>{t('voting.time_remaining')}</ProjectDesc>
                <ProjectValue>{gameTimer}</ProjectValue>
            </TimeWrapper>
            {hasStageButton && <NextStageButton disabled={gameStatus === GameStatuses.closed || isNextStageClicked} isClosed={gameStatus === GameStatuses.closed} onClick={onStageChange}>{t(isNextStageClicked ? 'voting.buttons.waiting' : gameStatus === GameStatuses.closed ? 'voting.buttons.game_ended' : 'voting.buttons.next_stage')}</NextStageButton>}
    </ProjectGameInfoWrapper>
    </FixedWrapper>, outer);
}